import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Monitor, Shield, Loader2, LogOut, AlertTriangle, RefreshCw, Clock,
  ShieldAlert, Check, X,
} from 'lucide-react';
import axios from 'axios';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtRelative = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffSec = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return then.toLocaleDateString();
};

const fmtAbsolute = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const SessionRow = ({ session, showOwner, onRevoke, revoking }) => {
  const isCurrent = session.is_current;
  const revoked = session.revoked;
  return (
    <div
      className={`bg-white border rounded-lg p-3 sm:p-4 ${isCurrent ? 'border-[#BA1826]' : 'border-[#E5E7EB]'}`}
      data-testid={`session-row-${session.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`p-2 rounded-md flex-shrink-0 ${isCurrent ? 'bg-[#FEF2F2] text-[#BA1826]' : 'bg-[#F8F9FA] text-[#4B5563]'}`}>
          <Monitor className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-heading text-sm font-bold text-[#0A0A0A] truncate">
              {session.device_label || 'Unknown device'}
            </h4>
            {isCurrent && (
              <span className="text-[10px] font-bold bg-[#BA1826] text-white px-1.5 py-0.5 rounded uppercase tracking-wider"
                data-testid="current-session-badge">
                This device
              </span>
            )}
            {revoked && (
              <span className="text-[10px] font-bold bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded uppercase tracking-wider">
                Revoked
              </span>
            )}
          </div>
          {showOwner && (
            <p className="text-xs text-[#0A0A0A] font-medium mt-0.5 truncate">{session.email}</p>
          )}
          <div className="text-[11px] text-[#4B5563] mt-1 grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-0.5">
            <span><strong>IP:</strong> {session.ip_address || '—'}</span>
            <span title={fmtAbsolute(session.created_at)}>
              <strong>Signed in:</strong> {fmtRelative(session.created_at)}
            </span>
            <span title={fmtAbsolute(session.last_activity_at)} className="flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              <strong>Active:</strong> {fmtRelative(session.last_activity_at)}
            </span>
          </div>
          {revoked && session.revoked_reason && (
            <p className="text-[10px] text-[#9CA3AF] mt-1">
              Revoked {fmtRelative(session.revoked_at)} · reason: {session.revoked_reason}
            </p>
          )}
        </div>
        {!revoked && (
          <button
            onClick={() => onRevoke(session)}
            disabled={revoking === session.id || isCurrent}
            title={isCurrent ? 'Use the Sign Out button to end this session' : 'Revoke this session'}
            className="text-xs font-semibold px-2.5 py-1.5 rounded border border-[#E5E7EB] text-[#BA1826] hover:bg-[#FEF2F2] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`revoke-session-${session.id}`}
          >
            {revoking === session.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
            Revoke
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * ActiveSessions panel.
 *
 * Props:
 *   scope: 'me' (default) — shows only the current user's sessions
 *          'all'          — Super-admin override, lists every admin session
 *   onSessionRevoked: optional callback fired after a successful revoke
 */
const ActiveSessions = ({ scope = 'me', onSessionRevoked }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSid, setCurrentSid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokingOthers, setRevokingOthers] = useState(false);
  const [includeRevoked, setIncludeRevoked] = useState(false);

  const endpoint = scope === 'all'
    ? `${BACKEND_URL}/api/admin/sessions/all${includeRevoked ? '?include_revoked=true' : ''}`
    : `${BACKEND_URL}/api/admin/sessions/me`;

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint, { withCredentials: true });
      setSessions(res.data.sessions || []);
      setCurrentSid(res.data.current_session_id || null);
    } catch (e) {
      logger.error(e);
      setSessions([]);
    }
    setLoading(false);
  }, [endpoint]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleRevoke = async (session) => {
    const ownerNote = scope === 'all' && session.email ? ` for ${session.email}` : '';
    if (!window.confirm(`Revoke this session${ownerNote}? The user on that device will be signed out immediately.`)) return;
    setRevoking(session.id);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/sessions/${session.id}/revoke`, {}, { withCredentials: true });
      await fetchSessions();
      onSessionRevoked?.(session);
    } catch (e) {
      alert('Failed to revoke session: ' + (e?.response?.data?.detail || 'unknown error'));
    }
    setRevoking(null);
  };

  const handleRevokeOthers = async (fromAlert = false) => {
    const confirmMsg = fromAlert
      ? 'Sign out ALL other sessions now? This will immediately revoke every other device signed in to your account.'
      : 'Sign out all other devices? You will stay signed in here.';
    if (!window.confirm(confirmMsg)) return;
    setRevokingOthers(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/sessions/revoke-others`,
        fromAlert ? { from_alert: true } : {},
        { withCredentials: true },
      );
      await fetchSessions();
      if (fromAlert) {
        alert(`Signed out ${res.data.revoked_count} other session${res.data.revoked_count === 1 ? '' : 's'}. Your account has been secured and the action was logged.`);
      } else {
        alert(`Signed out ${res.data.revoked_count} other session${res.data.revoked_count === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      alert('Failed: ' + (e?.response?.data?.detail || 'unknown error'));
    }
    setRevokingOthers(false);
  };

  // ---- Suspicious login detection (scope='me' only) -------------------------
  const [acknowledging, setAcknowledging] = useState(null);
  const suspiciousSessions = useMemo(
    () => sessions.filter(s => !s.revoked && s.is_new_ip && !s.is_current),
    [sessions],
  );

  const handleAcknowledge = async (session) => {
    setAcknowledging(session.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/sessions/${session.id}/acknowledge`,
        {},
        { withCredentials: true },
      );
      await fetchSessions();
    } catch (e) {
      alert('Failed to acknowledge: ' + (e?.response?.data?.detail || 'unknown error'));
    }
    setAcknowledging(null);
  };

  const activeCount = sessions.filter(s => !s.revoked).length;
  const otherActiveCount = sessions.filter(s => !s.revoked && !s.is_current).length;

  return (
    <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4 sm:p-6" data-testid={`active-sessions-${scope}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-heading text-base font-bold text-[#0A0A0A] flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#BA1826]" />
            {scope === 'all' ? 'All Admin Sessions' : 'Active Sessions'}
          </h3>
          <p className="text-xs text-[#4B5563] mt-0.5">
            {scope === 'all'
              ? 'Super Admin override — view and revoke any admin session across the platform.'
              : 'Devices currently signed in to your account. Revoke any you don’t recognize.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {scope === 'all' && (
            <label className="flex items-center gap-1 text-xs text-[#4B5563]">
              <input type="checkbox" checked={includeRevoked}
                onChange={(e) => setIncludeRevoked(e.target.checked)}
                className="accent-[#BA1826]" data-testid="include-revoked-toggle" />
              Show revoked
            </label>
          )}
          <button onClick={fetchSessions} disabled={loading}
            className="text-xs px-2.5 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-white flex items-center gap-1 disabled:opacity-50"
            data-testid="refresh-sessions-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Suspicious-login banner (scope=me only, shows once per non-current new-IP session) */}
      {scope === 'me' && suspiciousSessions.length > 0 && (
        <div
          className="bg-[#FEF2F2] border-2 border-[#BA1826] rounded-lg p-4 mb-3"
          data-testid="suspicious-login-banner"
        >
          <div className="flex items-start gap-3 mb-3">
            <ShieldAlert className="w-5 h-5 text-[#BA1826] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">
                ⚠ Suspicious login detected
              </h4>
              <p className="text-xs text-[#4B5563] mt-0.5">
                We spotted {suspiciousSessions.length === 1 ? 'a new sign-in' : `${suspiciousSessions.length} new sign-ins`} from an IP address your account has never used before.
                If this was you, dismiss the alert. If not, sign out all other sessions immediately.
              </p>
            </div>
          </div>

          {suspiciousSessions.map(s => (
            <div
              key={s.id}
              className="bg-white border border-[#FECACA] rounded-md p-3 mb-2"
              data-testid={`suspicious-session-${s.id}`}
            >
              <div className="flex items-start gap-2 flex-wrap">
                <div className="p-1.5 rounded bg-[#FEF2F2] text-[#BA1826] flex-shrink-0">
                  <Monitor className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#0A0A0A]">{s.device_label || 'Unknown device'}</p>
                  <div className="text-[11px] text-[#4B5563] mt-0.5 grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-0.5">
                    <span><strong>IP:</strong> {s.ip_address || '—'}</span>
                    <span title={fmtAbsolute(s.created_at)}>
                      <strong>Signed in:</strong> {fmtRelative(s.created_at)}
                    </span>
                    <span title={fmtAbsolute(s.last_activity_at)}>
                      <strong>Last active:</strong> {fmtRelative(s.last_activity_at)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleAcknowledge(s)}
                  disabled={acknowledging === s.id}
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1 disabled:opacity-50"
                  data-testid={`dismiss-suspicious-${s.id}`}
                  title="This was me — dismiss the alert and trust this IP"
                >
                  {acknowledging === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  This was me
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={() => handleRevokeOthers(true)}
            disabled={revokingOthers}
            className="w-full text-xs font-bold px-3 py-2 rounded bg-[#BA1826] text-white hover:bg-[#9A1320] flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="suspicious-revoke-all-btn"
          >
            {revokingOthers ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
            This wasn’t me — sign out all other sessions
          </button>
        </div>
      )}

      {scope === 'me' && otherActiveCount > 0 && suspiciousSessions.length === 0 && (
        <div className="bg-[#FEF9E7] border border-[#F4D03F] rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="revoke-others-banner">
          <AlertTriangle className="w-4 h-4 text-[#BA1826] flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-[#4B5563]">
            <strong className="text-[#0A0A0A]">{otherActiveCount} other device{otherActiveCount === 1 ? '' : 's'} signed in.</strong>{' '}
            If anything here looks unfamiliar, sign them all out now.
          </div>
          <button onClick={() => handleRevokeOthers(false)} disabled={revokingOthers}
            className="text-xs font-semibold px-2.5 py-1.5 rounded bg-[#BA1826] text-white hover:bg-[#9A1320] flex items-center gap-1 disabled:opacity-50"
            data-testid="revoke-others-btn">
            {revokingOthers ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
            Sign out other devices
          </button>
        </div>
      )}

      {loading && sessions.length === 0 ? (
        <div className="flex items-center justify-center py-6 text-[#4B5563]">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          No active sessions to display.
        </p>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <SessionRow
              key={s.id}
              session={s}
              showOwner={scope === 'all'}
              onRevoke={handleRevoke}
              revoking={revoking}
            />
          ))}
        </div>
      )}

      <p className="text-[10px] text-[#9CA3AF] mt-3">
        Showing {activeCount} active session{activeCount === 1 ? '' : 's'}
        {scope === 'me' && currentSid && ' · current device highlighted in red'}
      </p>
    </div>
  );
};

export default ActiveSessions;
